/**
 * The Project overview, once, for Teslyn Field, Teslyn Cloud and Teslyn Mobile (MB's R246, 5 Nov 2026;
 * docs/specs/project-reporting/overview-everywhere.md). `projectOverview` takes a project's rows and gives every
 * figure and word the Overview shows, in the order it shows them: the tiles, the Blocks table, the Day, Week, Month and
 * Project reviews, Aircraft and Pilots. Each app draws the result; none adds up anything itself, so the three cannot
 * disagree. Teslyn Field reads the rows from its own database, Teslyn Cloud and the phone from Teslyn Cloud with the
 * person's sign-in; the quick-start checklist is Teslyn Field's alone (decision 846, R246).
 *
 * The result is plain data: the phone keeps it as it is (`as at <time>`), so it opens at once and with no signal.
 *
 * The rows have to be complete. A read that failed or was refused is never passed in as an empty list: `qc: null` is
 * how a caller says the QC rows are not known, and the screens say so (the "no false none" rule of R225).
 */
import {
  MR_STATE_QC,
  MR_STATE_WORD,
  aircraftFuelType,
  aircraftHours,
  maxFlightHours,
  mrStatus,
  mrWarnHours,
  type MrState,
} from './aircraft.js';
import { dayTotals, entryDates, entryRows, pilotSummaries, type DayTotals } from './day-summary.js';
import { FUEL_TYPE_LABEL } from './fuel.js';
import { isoWeekOf } from './dates.js';
import type {
  Aircraft,
  Block,
  DailyEntry,
  EntryType,
  Flight,
  LineStatus,
  Person,
  PlannedLine,
} from './model.js';
import { opsLines } from './ops.js';
import {
  periodReviews,
  type PeriodRow,
  type PeriodTotals,
  type ReviewKind,
  type ReviewPeriod,
} from './period-review.js';
import { flownCreditTotalM } from './progress.js';
import {
  daysExplained,
  estimateDaysText,
  estimateRemainingKm,
  estimateRemainingKmByBlock,
  flightHoursExplained,
  projectEstimate,
  type ProjectEstimate,
} from './project-estimate.js';
import { qcActioned, qcFlightsPending, type QcActionedInput } from './qc-actioned.js';
import { blockPlannedKm, plannedLineRole } from './reflights.js';
import { reportTotals, type ReportPeriod } from './report-totals.js';
import { entryCrewName, shortName, type EntryCrewSlot } from './staff.js';

// ------------------------------------------------------------------------------------------------ numbers as text

/**
 * `value` to `digits` decimals, rounded half away from zero on its shortest decimal, as `toLocaleString('en-AU')`
 * rounds it. Written out because the three engines differ: Node and WebView2 have the full `Intl`, Hermes does not
 * promise it on every phone, and the same figure must read the same in all three apps.
 */
function fixedDigits(value: number, digits: number): string {
  const text = String(value);
  if (text.includes('e')) return value.toFixed(digits);
  const [whole = '0', frac = ''] = text.split('.');
  if (frac.length <= digits) return digits > 0 ? `${whole}.${frac.padEnd(digits, '0')}` : whole;
  const kept = [...whole, ...frac.slice(0, digits)].map(Number);
  if (Number(frac[digits]) >= 5) {
    let i = kept.length - 1;
    for (; i >= 0; i--) {
      if (kept[i] === 9) kept[i] = 0;
      else {
        kept[i] = kept[i]! + 1;
        break;
      }
    }
    if (i < 0) kept.unshift(1);
  }
  const out = kept.join('');
  const cut = out.length - digits;
  return digits > 0 ? `${out.slice(0, cut)}.${out.slice(cut)}` : out;
}

/** A number with thousands separators and exactly `digits` decimals, e.g. `1,234.56`. */
export function auNumber(value: number, digits: number): string {
  if (!Number.isFinite(value)) return value.toLocaleString('en-AU');
  const negative = value < 0 || Object.is(value, -0);
  const [whole = '0', frac] = fixedDigits(Math.abs(value), digits).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+$)/g, ',');
  return `${negative ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
}

/** Kilometres with thousands separators and two decimals, e.g. `1,234.56`. */
export const overviewKm = (value: number): string => auNumber(value, 2);

/** A figure with `digits` decimals; `–` when it is not known. */
export function overviewNum(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return auNumber(value, digits);
}

/** A percentage to one decimal, e.g. `43.2%`. */
export const overviewPct = (value: number): string => `${value.toFixed(1)}%`;

// ------------------------------------------------------------------------------------------------ block progress

export interface OverviewBlockProgress {
  readonly block: Block;
  readonly plannedKm: number;
  readonly flownKm: number;
  readonly acceptedKm: number;
  /** Accepted as a percentage of planned, at most 100 (as the weekly report, `km.ts`); 0 when nothing is planned. */
  readonly pct: number;
  /**
   * Km still to fly (MB's R118): the estimate tiles' rule, `estimateRemainingKmByBlock` (reflights and patches
   * included, lines awaiting review counted as done), so the blocks add up to the km the estimate works from.
   */
  readonly remainingKm: number;
  readonly lines: number;
  readonly pending: number;
  readonly rejected: number;
}

export interface OverviewProgress {
  readonly blocks: readonly OverviewBlockProgress[];
  readonly plannedKm: number;
  readonly flownKm: number;
  readonly acceptedKm: number;
  readonly pct: number;
  readonly pending: number;
  readonly rejected: number;
}

/** A period that covers every flight to date: the ISO week of the latest flight (or of today). */
export function toDatePeriod(flights: readonly Flight[], today: Date = new Date()): ReportPeriod {
  const latest = flights.reduce<string | null>((m, f) => (m === null || f.date > m ? f.date : m), null);
  const d = latest ? new Date(`${latest}T00:00:00Z`) : today;
  const w = isoWeekOf(Number.isNaN(d.getTime()) ? today : d);
  return { week: w.week, year: w.isoYear };
}

const liveStatus = (s: LineStatus): boolean => s.deleted_at === null;

/** Accepted km as a percentage of planned, capped at 100 % (more can be accepted than planned). */
const percentOf = (acceptedKm: number, plannedKm: number): number =>
  plannedKm > 0 ? Math.min(100, (100 * acceptedKm) / plannedKm) : 0;

/**
 * Block progress from reviewed line status (ADR-013). Accepted km come from `reportTotals`, the only km rule; this adds
 * the display counts (flown km on the decision 32 basis, `flownCreditTotalM`; lines, pending, rejected).
 */
export function blockProgress(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  flights: readonly Flight[],
): OverviewProgress {
  const totals = reportTotals(statuses, plan, blocks, flights, toDatePeriod(flights));
  // N32: a block marked "km completed" has 0 km remaining
  const remaining = estimateRemainingKmByBlock(statuses, plan, blocks);
  const blockOfLine = new Map(plan.map((l) => [l.id, l.block_id]));
  const plannedM = new Map<string, number>();
  // original lines only: reflights and patches never add to the planned km (decision 29)
  for (const l of plan.filter((p) => p.deleted_at === null && plannedLineRole(p) === 'original'))
    plannedM.set(l.block_id, (plannedM.get(l.block_id) ?? 0) + l.length_m);
  const rows = blocks.map((block): OverviewBlockProgress => {
    const mine = statuses.filter((s) => liveStatus(s) && blockOfLine.get(s.planned_line_id) === block.id);
    const acceptedKm = totals.todateKm[block.key] ?? 0;
    const fromPlan = (plannedM.get(block.id) ?? 0) / 1000;
    // decision 1052: the flight plan's km and the block's infill lines (`fromPlan` holds them already)
    const plannedKm = block.planned_km > 0 ? blockPlannedKm(block, plan) : fromPlan;
    return {
      block,
      plannedKm,
      flownKm: flownCreditTotalM(mine) / 1000,
      acceptedKm,
      pct: percentOf(acceptedKm, plannedKm),
      remainingKm: remaining.get(block.id) ?? 0,
      lines: mine.length,
      pending: mine.filter((s) => s.acceptance === 'pending').length,
      rejected: mine.filter((s) => s.acceptance === 'rejected').length,
    };
  });
  const plannedKm = rows.reduce((a, r) => a + r.plannedKm, 0);
  const acceptedKm = rows.reduce((a, r) => a + r.acceptedKm, 0);
  return {
    blocks: rows,
    plannedKm,
    flownKm: rows.reduce((a, r) => a + r.flownKm, 0),
    acceptedKm,
    pct: percentOf(acceptedKm, plannedKm),
    pending: rows.reduce((a, r) => a + r.pending, 0),
    rejected: rows.reduce((a, r) => a + r.rejected, 0),
  };
}

/**
 * Est. flight hours and days remaining (MB's R100): `projectEstimate`, the rule the Operations Hub's figures are built
 * from, on the project's own line statuses, daily entries and remaining km (`estimateRemainingKm`: `planProgress`,
 * decision 29, with lines awaiting review counted as done, decision 716).
 */
export function overviewEstimate(
  projectId: string,
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  flights: readonly Flight[],
  entries: readonly DailyEntry[],
  /** N32: blocks marked "km completed" count 0 km remaining. */
  blocks: readonly Block[] = [],
): ProjectEstimate {
  const remainingKm = estimateRemainingKm(statuses, plan, blocks);
  return projectEstimate(
    projectId,
    opsLines(statuses, flights),
    // the estimate never reads the comments; the phone and a pilot are not sent them (OVERVIEW_COLUMNS)
    entries.filter((e) => e.deleted_at === null).map((e) => ({ ...e, op_comment: e.op_comment ?? '' })),
    remainingKm,
  );
}

// ------------------------------------------------------------------------------------------------ the tiles

export interface OverviewTile {
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  /** The bar and the sentence under it ("62.0% of 2,072.00 km planned"). */
  readonly progress?: { readonly fraction: number; readonly text: string };
  /** The line under the figure: how it was worked out, or why there is none yet. */
  readonly status?: string;
  /** How the figure is worked out, one sentence a line. */
  readonly hint?: readonly string[];
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The two estimate tiles (MB's R100): "—" and the reason until there is enough history. */
function estimateTiles(e: ProjectEstimate): OverviewTile[] {
  if (!e.ready)
    return [
      { label: 'Est. flight hours remaining', value: '—', status: e.reason },
      { label: 'Est. days remaining', value: '—', status: e.reason },
    ];
  return [
    {
      label: 'Est. flight hours remaining',
      value: overviewNum(e.flightHoursRemaining, 1),
      unit: 'h',
      status: `At ${overviewKm(e.kmPerSurveyHour)} km per survey hour, from ${plural(e.flyingDays, 'flying day')}`,
      hint: flightHoursExplained(e),
    },
    {
      label: 'Est. days remaining',
      value: estimateDaysText(e),
      unit: e.days[1] === 1 ? 'day' : 'days',
      status: `From ${plural(e.flyingDays, 'flying day')} and ${plural(e.daysOnJob, 'day')} on the job`,
      hint: daysExplained(e),
    },
  ];
}

function overviewTiles(p: OverviewProgress, estimate: ProjectEstimate): OverviewTile[] {
  return [
    { label: 'Planned', value: overviewKm(p.plannedKm), unit: 'km' },
    { label: 'Flown', value: overviewKm(p.flownKm), unit: 'km' },
    {
      label: 'Accepted',
      value: overviewKm(p.acceptedKm),
      unit: 'km',
      progress: {
        fraction: p.pct / 100,
        text: `${overviewPct(p.pct)} of ${overviewKm(p.plannedKm)} km planned`,
      },
    },
    { label: 'Lines to review', value: String(p.pending), unit: 'lines' },
    { label: 'Rejected lines', value: String(p.rejected), unit: 'lines' },
    ...estimateTiles(estimate),
  ];
}

// ------------------------------------------------------------------------------------------------ the sections

/** A chip's state and word; `pass` and `pending` as the Blocks table, all four for the aircraft's MR. */
export interface OverviewChip {
  readonly state: 'pass' | 'warn' | 'fail' | 'pending';
  readonly word: string;
}

export interface OverviewBlockRow {
  readonly id: string;
  readonly name: string;
  readonly plannedKm: number;
  readonly flownKm: number;
  readonly acceptedKm: number;
  readonly pct: number;
  readonly remainingKm: number;
  readonly chip: OverviewChip;
}

function blockChip(b: OverviewBlockProgress): OverviewChip {
  if (b.plannedKm > 0 && b.plannedKm - b.acceptedKm <= 0.005 * b.plannedKm)
    return { state: 'pass', word: 'Complete' };
  if (b.lines > 0) return { state: 'pending', word: 'In progress' };
  return { state: 'pending', word: 'Not started' };
}

const TYPE_WORD: Readonly<Record<EntryType, string>> = {
  flight: 'Flight',
  standby: 'Standby',
  maintenance: 'Maintenance',
  suspended: 'Suspended',
};

/** One row of the Day review. */
export interface OverviewDayRow {
  readonly id: string;
  readonly flt: string;
  readonly rego: string;
  /** "Flight", or "Standby 1.0 d". */
  readonly entry: string;
  readonly takeoff: string;
  readonly landing: string;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  readonly flown_h: number;
  readonly prog_ttis_h: number | null;
  readonly fuel_used_l: number;
  readonly pilots: string;
  readonly operators: string;
  readonly flown_km: number;
  readonly accepted_km: number;
}

export interface OverviewDay {
  readonly date: string;
  /** "3 flights, 8.0 h flown, 1,234.00 km accepted". */
  readonly summary: string;
  readonly rows: readonly OverviewDayRow[];
  readonly totals: DayTotals;
}

export interface OverviewPeriod extends ReviewPeriod {
  readonly summary: string;
  readonly rows: readonly PeriodRow[];
  readonly totals: PeriodTotals;
  /** A Suspended (d) column is shown only when the period has suspended days. */
  readonly suspended: boolean;
}

export interface OverviewAircraftRow {
  readonly id: string;
  readonly registration: string;
  /** The fuel's name, "–" when not known. */
  readonly fuel: string;
  readonly max_flight_h: number | null;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  readonly total_h: number;
  readonly ttis_now_h: number | null;
  readonly to_next_mr_h: number | null;
  readonly mr: OverviewChip;
  /** "Issued 5,070.3 · Job start 5,085.9", "–" with none. */
  readonly history: string;
}

export interface OverviewPilotRow {
  readonly id: string;
  readonly name: string;
  readonly flights: number;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  readonly total_h: number;
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly refly_km: number;
  readonly km_per_h: number;
}

/** Everything the Overview shows, in its order. Plain data: the phone keeps it as it is. */
export interface ProjectOverview {
  readonly tiles: readonly OverviewTile[];
  readonly blocks: readonly OverviewBlockRow[];
  /** Dates with entries, newest first. */
  readonly days: readonly OverviewDay[];
  /** Each review's periods, newest first (`reviewPeriods`). */
  readonly reviews: Readonly<Record<ReviewKind, readonly OverviewPeriod[]>>;
  readonly aircraft: readonly OverviewAircraftRow[];
  readonly pilots: readonly OverviewPilotRow[];
  /** Flights with Flight data QC actions pending (decision 751); null when the QC rows are not known. */
  readonly qcPending: number | null;
}

export interface OverviewQcRows {
  readonly runs: QcActionedInput['runs'];
  readonly lines: QcActionedInput['lines'];
  readonly acceptances: QcActionedInput['acceptances'];
}

/** A project's rows, complete and as read: never a partial or failed read (the top of this file). */
export interface OverviewRows {
  readonly project: { readonly id: string; readonly mr_warn_h?: number | null };
  readonly blocks: readonly Block[];
  readonly plan: readonly PlannedLine[];
  readonly statuses: readonly LineStatus[];
  readonly flights: readonly Flight[];
  readonly entries: readonly DailyEntry[];
  /** The organisation's live aircraft. */
  readonly aircraft: readonly Aircraft[];
  /** The register, deleted people too: the records that name them keep the name (decision 602). */
  readonly people: readonly Pick<Person, 'id' | 'name'>[];
  /** Null when not known; absent in Teslyn Field, which counts them for its menu too (`useQcPending`). */
  readonly qc?: OverviewQcRows | null;
}

/** "3 flights, 8.0 h flown, 1,234.00 km accepted". */
const periodWords = (t: {
  readonly flights: number;
  readonly hours_flown_h: number;
  readonly accepted_km: number;
}): string =>
  `${t.flights} flight${t.flights === 1 ? '' : 's'}, ${overviewNum(t.hours_flown_h, 1)} h flown, ${overviewKm(t.accepted_km)} km accepted`;

/** The MR state's chip (MRs are tracked by hours, not dates, MB 19 Oct): amber at the project's flag, red when none are left. */
export const mrChip = (state: MrState): OverviewChip => ({
  state: MR_STATE_QC[state],
  word: MR_STATE_WORD[state],
});

/** The Aircraft section (TJR Summary "Aircraft" box and MR list). */
export function overviewAircraft(
  aircraft: readonly Aircraft[],
  entries: readonly DailyEntry[],
  warnH: number,
): OverviewAircraftRow[] {
  return aircraft.map((a) => {
    const mr = mrStatus(a, entries, warnH);
    const h = aircraftHours(a, entries);
    const fuel = aircraftFuelType(a);
    return {
      id: a.id,
      registration: a.registration,
      fuel: fuel ? FUEL_TYPE_LABEL[fuel] : '–',
      max_flight_h: maxFlightHours(a),
      survey_h: h.survey_h,
      comp_box_h: h.comp_box_h,
      cal_h: h.cal_h,
      ferry_h: h.ferry_h,
      total_h: h.total_h,
      ttis_now_h: mr.ttis_now_h,
      to_next_mr_h: mr.remaining_h,
      mr: mrChip(mr.state),
      history:
        mr.list.length === 0 ? '–' : mr.list.map((m) => `${m.label} ${overviewNum(m.ttis_h, 1)}`).join(' · '),
    };
  });
}

/** The project's overview: see the top of this file. */
export function projectOverview(rows: OverviewRows): ProjectOverview {
  const { blocks, plan, statuses, flights, entries, aircraft, people } = rows;
  const progress = blockProgress(
    statuses,
    plan,
    blocks.filter((b) => b.kind === 'survey'),
    flights,
  );
  const estimate = overviewEstimate(rows.project.id, statuses, plan, flights, entries, blocks);

  const rego = (id: string | null): string => aircraft.find((a) => a.id === id)?.registration ?? '–';
  // a person removed for good from the register keeps the name the entry kept (decision 602)
  const names = (e: DailyEntry, slots: readonly EntryCrewSlot[]): string =>
    slots
      .map((slot) => entryCrewName(people, e, slot))
      .filter((n): n is string => n !== null)
      .map(shortName)
      .join(', ');
  const live = entries.filter((e) => e.deleted_at === null);
  const rowsOfDay = new Map<string, ReturnType<typeof entryRows>>();
  for (const r of entryRows(live, aircraft, statuses, flights)) {
    const list = rowsOfDay.get(r.entry.date) ?? [];
    list.push(r);
    rowsOfDay.set(r.entry.date, list);
  }
  const days = entryDates(live).map((date): OverviewDay => {
    const own = rowsOfDay.get(date) ?? [];
    const totals = dayTotals(own);
    return {
      date,
      summary: periodWords(totals),
      totals,
      rows: own.map((r) => ({
        id: r.entry.id,
        flt: r.entry.flt_num === null ? '' : String(r.entry.flt_num),
        rego: rego(r.entry.aircraft_id),
        entry:
          TYPE_WORD[r.entry.entry_type] +
          (r.entry.entry_type !== 'flight' ? ` ${overviewNum(r.entry.days_alloc, 1)} d` : ''),
        takeoff: r.entry.takeoff_time ?? '',
        landing: r.landing ?? '',
        survey_h: r.entry.svy_hours,
        comp_box_h: r.entry.cbx_hours,
        cal_h: r.entry.cal_hours,
        ferry_h: r.entry.fry_hours,
        flown_h: r.hours_flown_h,
        prog_ttis_h: r.prog_ttis_h,
        fuel_used_l: r.entry.fuel_used_l,
        pilots: names(r.entry, ['pilot', 'pilot2']),
        operators: names(r.entry, ['operator1', 'operator2']),
        flown_km: r.flown_km,
        accepted_km: r.accepted_km,
      })),
    };
  });

  const review = (kind: ReviewKind): OverviewPeriod[] =>
    periodReviews(kind, live, statuses, flights).map((p) => ({
      ...p.period,
      summary: periodWords(p.totals),
      rows: p.rows,
      totals: p.totals,
      suspended: p.totals.suspended_days > 0,
    }));

  const pilots = pilotSummaries(live, statuses, flights).map((p): OverviewPilotRow => ({
    id: p.person_id,
    name: p.kept_name ?? people.find((x) => x.id === p.person_id)?.name ?? 'Unknown pilot',
    flights: p.flights,
    survey_h: p.survey_h,
    comp_box_h: p.comp_box_h,
    cal_h: p.cal_h,
    ferry_h: p.ferry_h,
    total_h: p.total_h,
    flown_km: p.flown_km,
    accepted_km: p.accepted_km,
    refly_km: p.refly_km,
    km_per_h: p.km_per_h,
  }));

  return {
    tiles: overviewTiles(progress, estimate),
    blocks: progress.blocks.map((b) => ({
      id: b.block.id,
      name: b.block.name,
      plannedKm: b.plannedKm,
      flownKm: b.flownKm,
      acceptedKm: b.acceptedKm,
      pct: b.pct,
      remainingKm: b.remainingKm,
      chip: blockChip(b),
    })),
    days,
    reviews: { week: review('week'), month: review('month'), project: review('project') },
    aircraft: overviewAircraft(aircraft, live, mrWarnHours(rows.project)),
    pilots,
    qcPending: rows.qc
      ? qcFlightsPending(
          qcActioned({
            runs: rows.qc.runs,
            lines: rows.qc.lines,
            acceptances: rows.qc.acceptances,
            statuses: statuses.filter(liveStatus),
            plan,
          }),
        )
      : null,
  };
}

// ------------------------------------------------------------------------------------------------ the columns

/**
 * One column of a section: its heading, its text, and how it is set (`figure`: a number, right aligned in IBM Plex
 * Mono; `data`: a code, in IBM Plex Mono; `text`: words). Teslyn Cloud's tables and the phone's cards are drawn from
 * these, so they have Teslyn Field's columns in Teslyn Field's order.
 */
export interface OverviewCell {
  readonly label: string;
  readonly text: string;
  readonly kind: 'figure' | 'data' | 'text';
}

const figure = (label: string, text: string): OverviewCell => ({ label, text, kind: 'figure' });
const hours = (label: string, v: number | null): OverviewCell => figure(label, overviewNum(v, 1));
const kmCell = (label: string, v: number): OverviewCell => figure(label, overviewKm(v));

export const blockCells = (b: OverviewBlockRow): readonly OverviewCell[] => [
  { label: 'Block', text: b.name, kind: 'text' },
  kmCell('Planned km', b.plannedKm),
  kmCell('Flown km', b.flownKm),
  kmCell('Accepted km', b.acceptedKm),
  figure('Accepted', overviewPct(b.pct)),
  kmCell('Remaining km', b.remainingKm),
];

/** A Day review row, every column in order. */
export const dayCells = (r: OverviewDayRow): readonly OverviewCell[] => [
  { label: 'Flt', text: r.flt, kind: 'data' },
  { label: 'Aircraft', text: r.rego, kind: 'data' },
  { label: 'Entry', text: r.entry, kind: 'text' },
  { label: 'T/O', text: r.takeoff, kind: 'data' },
  { label: 'Land', text: r.landing, kind: 'data' },
  hours('Survey (h)', r.survey_h),
  hours('Comp box (h)', r.comp_box_h),
  hours('Cals (h)', r.cal_h),
  hours('Ferry (h)', r.ferry_h),
  hours('Flown (h)', r.flown_h),
  hours('Prog TTIS (h)', r.prog_ttis_h),
  figure('Fuel used (L)', overviewNum(r.fuel_used_l)),
  { label: 'Pilots', text: r.pilots, kind: 'text' },
  { label: 'Operators', text: r.operators, kind: 'text' },
  kmCell('Km flown', r.flown_km),
  kmCell('Km accepted', r.accepted_km),
];

/** The Day total: the columns after Land; blank under Prog TTIS, Pilots and Operators. */
export const dayTotalCells = (t: DayTotals): readonly OverviewCell[] => [
  hours('Survey (h)', t.survey_h),
  hours('Comp box (h)', t.comp_box_h),
  hours('Cals (h)', t.cal_h),
  hours('Ferry (h)', t.ferry_h),
  hours('Flown (h)', t.hours_flown_h),
  figure('Prog TTIS (h)', ''),
  figure('Fuel used (L)', overviewNum(t.fuel_used_l)),
  { label: 'Pilots', text: '', kind: 'text' },
  { label: 'Operators', text: '', kind: 'text' },
  kmCell('Km flown', t.flown_km),
  kmCell('Km accepted', t.accepted_km),
];

/** A row of a Week, Month or Project review, without its Day or Week heading. */
export const periodCells = (t: PeriodTotals, suspended: boolean): readonly OverviewCell[] => [
  figure('Flights', String(t.flights)),
  figure('Days flown', String(t.days_flown)),
  hours('Survey (h)', t.survey_h),
  hours('Comp box (h)', t.comp_box_h),
  hours('Cals (h)', t.cal_h),
  hours('Ferry (h)', t.ferry_h),
  hours('Flown (h)', t.hours_flown_h),
  figure('Fuel used (L)', overviewNum(t.fuel_used_l)),
  kmCell('Km flown', t.flown_km),
  kmCell('Km accepted', t.accepted_km),
  kmCell('Km rejected', t.rejected_km),
  figure('Lines', String(t.lines)),
  hours('Standby (d)', t.standby_days),
  hours('Maintenance (d)', t.maintenance_days),
  ...(suspended ? [hours('Suspended (d)', t.suspended_days)] : []),
];

/** An aircraft's columns but its MR chip, which comes last but one, before the MR history. */
export const aircraftCells = (a: OverviewAircraftRow): readonly OverviewCell[] => [
  { label: 'Aircraft', text: a.registration, kind: 'data' },
  { label: 'Fuel', text: a.fuel, kind: 'text' },
  hours('Max flight (h)', a.max_flight_h),
  hours('Survey (h)', a.survey_h),
  hours('Comp box (h)', a.comp_box_h),
  hours('Cals (h)', a.cal_h),
  hours('Ferry (h)', a.ferry_h),
  hours('Total flown (h)', a.total_h),
  hours('TTIS now (h)', a.ttis_now_h),
  hours('To next MR (h)', a.to_next_mr_h),
];

export const pilotCells = (p: OverviewPilotRow): readonly OverviewCell[] => [
  { label: 'Pilot', text: p.name, kind: 'text' },
  figure('Flights', String(p.flights)),
  hours('Survey (h)', p.survey_h),
  hours('Comp box (h)', p.comp_box_h),
  hours('Cals (h)', p.cal_h),
  hours('Ferry (h)', p.ferry_h),
  hours('Total (h)', p.total_h),
  kmCell('Km flown', p.flown_km),
  kmCell('Km accepted', p.accepted_km),
  kmCell('Refly km', p.refly_km),
  kmCell('Accepted km/h', p.km_per_h),
];

// ------------------------------------------------------------------------------------------------ the words

/** The Blocks table's "Remaining km" (R118), worked out as the estimate tiles' km. */
export const REMAINING_KM_TITLE =
  'Km still to fly in the block: planned lines not yet flown or not accepted, reflights and patches included; lines awaiting review count as done, as in the estimate tiles.';

export const OVERVIEW_NO_BLOCKS = {
  title: 'No blocks yet',
  body: 'Blocks and planned lines are read from the flight-plan files in the project folder.',
} as const;
export const OVERVIEW_NO_ENTRIES = {
  title: 'No daily entries yet',
  body: "Each day's flights and standby appear here once entered.",
} as const;
export const OVERVIEW_NO_PILOTS = {
  title: 'No flights yet',
  body: 'Pilot hours and km appear after the first flight.',
} as const;

/** The Overview's sections that stay on the laptop, and why (R246: shown as not available, with a plain reason). */
export interface OverviewNotHere {
  readonly item: string;
  readonly reason: string;
}

const LAPTOP_ONLY: readonly OverviewNotHere[] = [
  {
    item: 'New daily entry',
    reason: 'Daily entries are made in Teslyn Field, on the laptop that holds the project.',
  },
  {
    item: 'Flight logs found',
    reason:
      "Teslyn Field's folder watch finds new flight logs in the project folder on the laptop, so they show there only.",
  },
];

/** What Teslyn Cloud's or the phone's Overview leaves out of Teslyn Field's, and why. */
export function overviewNotHere(app: 'cloud' | 'mobile'): readonly OverviewNotHere[] {
  if (app === 'cloud') return LAPTOP_ONLY;
  return [
    ...LAPTOP_ONLY,
    { item: 'Export CSV', reason: 'The daily columns are exported from Teslyn Field or Teslyn Cloud.' },
  ];
}

/** Under Teslyn Cloud's and the phone's Overview: where the figures come from. */
export const OVERVIEW_SOURCE_WORDS =
  'These figures are what Teslyn Field has synced to Teslyn Cloud. A change on a laptop shows here after that laptop syncs.';

/** The QC rows could not be read: the count is not known, which is not "none". */
export const OVERVIEW_QC_UNKNOWN_WORDS =
  'QC actions pending: not available. The QC results could not be read.';

/** The phone's words for a read that has never worked here (no false none, R225). */
export const OVERVIEW_NEVER_READ_WORDS =
  'The Overview has not been read on this phone yet. It needs a signal once, then it opens with no signal too.';

/** A pilot sign-in before the database update (20261105170000) is applied: nothing to show, and why. */
export const OVERVIEW_PILOT_NOT_READY_WORDS =
  'The Overview for pilot sign-ins needs an update to Teslyn Cloud that has not been applied yet. Your figures are not lost: they show once it is.';

// ------------------------------------------------------------------------------------------------ what is read

/**
 * The columns the Overview needs from each table: all the phone reads (its data allowance), all a pilot-only sign-in
 * is given (`pilot_overview_rows`, supabase 20261105170000; that function names the same columns), and no more.
 */
export const OVERVIEW_COLUMNS = {
  block: ['id', 'name', 'key', 'digit', 'kind', 'planned_km', 'km_completed', 'deleted_at'],
  planned_line: [
    'id',
    'block_id',
    'number',
    'kind',
    'length_m',
    'reflight_of_line_id',
    'segment_from_m',
    'segment_to_m',
    'deleted_at',
  ],
  line_status: [
    'id',
    'project_id',
    'flight_id',
    'planned_line_id',
    'line_number',
    'flown_m',
    'proposed_m',
    'accepted_m',
    'acceptance',
    'use_km',
    'reason',
    'note',
    'reviewed_by',
    'reviewed_at',
    'log_completion',
    'deleted_at',
  ],
  flight: ['id', 'date', 'flt_num', 'aircraft_id', 'rego', 'deleted_at'],
  daily_entry: [
    'id',
    'project_id',
    'date',
    'aircraft_id',
    'pilot_id',
    'pilot2_id',
    'operator1_id',
    'operator2_id',
    'pilot_name',
    'pilot2_name',
    'operator1_name',
    'operator2_name',
    'entry_type',
    'standby_type',
    'flt_num',
    'takeoff_time',
    'days_alloc',
    'svy_hours',
    'cbx_hours',
    'cal_hours',
    'fry_hours',
    'ext_hours',
    'fuel_used_l',
    'fuel_load_l',
    'new_mr_ttis_h',
    'mobilization',
    'deleted_at',
  ],
  aircraft: [
    'id',
    'registration',
    'fuel_flow_lph',
    'mr_interval_h',
    'ttis_at_issue_h',
    'ttis_at_start_h',
    'legacy_json',
    'fuel_type',
    'max_flight_h',
    'fuel_capacity_l',
    'deleted_at',
  ],
  person: ['id', 'name'],
  qc_run: ['id', 'flight_id', 'status', 'started_at', 'source_path', 'deleted_at'],
  qc_line_result: ['id', 'run_id', 'line_status_id', 'line_name', 'verdict', 'deleted_at'],
  qc_acceptance: ['id', 'qc_line_result_id', 'action', 'by_name', 'acted_at', 'created_at', 'deleted_at'],
} as const;

export type OverviewTable = keyof typeof OVERVIEW_COLUMNS;

/** A table's columns as PostgREST's `select` takes them. */
export const overviewSelect = (table: OverviewTable): string => OVERVIEW_COLUMNS[table].join(',');
